import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Card,
  CardContent
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add, Delete, Visibility, Edit } from '@mui/icons-material';
import { createIdentifiedCampaign, getIdentifiedCampaigns, deleteIdentifiedCampaign, updateIdentifiedCampaign } from '../services/api';

function IdentifiedInfluencers() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [filteredCampaigns, setFilteredCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [campaignName, setCampaignName] = useState('');
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const response = await getIdentifiedCampaigns();
      const data = response.data.data || [];
      setCampaigns(data);
      setFilteredCampaigns(data);
    } catch (error) {
      console.error('Failed to fetch campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
    if (!value.trim()) {
      setFilteredCampaigns(campaigns);
    } else {
      const filtered = campaigns.filter(campaign =>
        campaign.campaignName.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredCampaigns(filtered);
    }
  };

  const handleCreateCampaign = async () => {
    if (!campaignName.trim()) return;
    try {
      if (editingCampaign) {
        await updateIdentifiedCampaign(editingCampaign._id, { campaignName });
      } else {
        await createIdentifiedCampaign({ campaignName });
      }
      setOpenDialog(false);
      setCampaignName('');
      setEditingCampaign(null);
      fetchCampaigns();
    } catch (error) {
      console.error('Failed to save campaign:', error);
    }
  };

  const handleEditCampaign = (campaign) => {
    setEditingCampaign(campaign);
    setCampaignName(campaign.campaignName);
    setOpenDialog(true);
  };

  const handleDeleteCampaign = async (id) => {
    if (window.confirm('Are you sure you want to delete this campaign?')) {
      try {
        await deleteIdentifiedCampaign(id);
        fetchCampaigns();
      } catch (error) {
        console.error('Failed to delete campaign:', error);
      }
    }
  };

  const columns = [
    { field: 'sno', headerName: 'SNO', width: 100 },
    { field: 'campaignName', headerName: 'Campaign Name', width: 400 },
    {
      field: 'createdAt',
      headerName: 'Created At',
      width: 250,
      renderCell: (params) => {
        const date = new Date(params.value);
        return date.toLocaleString();
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => navigate(`/Identified-Influencers/${params.row._id}`)}
            sx={{ color: '#DAFF7C' }}
          >
            <Visibility />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleEditCampaign(params.row)}
            sx={{ color: '#1a1d29' }}
          >
            <Edit />
          </IconButton>
        </Box>
      )
    }
  ];

  const rows = filteredCampaigns.map((campaign, index) => ({
    id: campaign._id,
    sno: index + 1,
    campaignName: campaign.campaignName,
    createdAt: campaign.createdAt,
    _id: campaign._id
  }));

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" component="h1" sx={{ fontWeight: 600 }}>
          Identified Influencers
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            placeholder="Search campaigns..."
            value={searchText}
            onChange={(e) => handleSearch(e.target.value)}
            size="small"
          // sx={{ width: 300 }}
          />
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setOpenDialog(true)}
            sx={{ p: 0.9 }}
          >
            Add Campaign
          </Button>
        </Box>
      </Box>

      <Card>
        <CardContent>
          <DataGrid
            columns={columns}
            rows={rows}
            loading={loading}
            pageSize={10}
            disableRowSelectionOnClick
            autoHeight
            sx={{
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f5f5f5',
                fontWeight: 600
              }
            }}
          />
        </CardContent>
      </Card>

      <Dialog open={openDialog} onClose={() => { setOpenDialog(false); setEditingCampaign(null); setCampaignName(''); }} maxWidth="sm" fullWidth>
        <DialogTitle>{editingCampaign ? 'Edit Campaign' : 'Add New Campaign'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Campaign Name"
            value={campaignName}
            onChange={(e) => setCampaignName(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenDialog(false); setEditingCampaign(null); setCampaignName(''); }}>Cancel</Button>
          <Button onClick={handleCreateCampaign} variant="contained">
            {editingCampaign ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default IdentifiedInfluencers;
